"use client";

/**
 * DialIQ — Settings
 *
 * Renders as a toggle section inside the Dialer Preferences card.
 * Matches the visual pattern of the other preference sections
 * (left accent bar, icon tile, label + description, Switch, expand animation).
 *
 * Opinionated feature. Only two things configurable per user:
 *   1. Master on/off
 *   2. Excluded lead statuses (including custom pipeline stages)
 *
 * Auto-saves on change (debounced), no manual Save button.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, Loader2, AlertCircle, Ban } from "lucide-react";
import { cn } from "@/app/_module/lib/utils";
import {
  DEFAULT_USER_SETTINGS,
  type DialIQUserSettings,
} from "@/app/_module/services/redialService.types";
import { DialIQMark } from "@/app/(authenticated)/leads/_modules/components/DialIQMark";
import { Switch } from "@/app/_module/components/ui/switch";
import { Label } from "@/app/_module/components/ui/label";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import { usePipelineStages } from "@/app/(authenticated)/leads/_modules/hooks/queries";

// ────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────

const DEFAULT_EXCLUDABLE_STATUSES = [
  "Do not call",
  "Bad number",
  "Sold & Approved",
  "Underwriting",
  "Appt",
  "Lapsed",
  "Declined",
  "No sale",
];

const expand = {
  initial: { height: 0, opacity: 0 },
  animate: { height: "auto", opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.25, ease: [0.22, 1, 0.36, 1] as any },
};

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

interface DialIQSettingsProps {
  initial?: Partial<DialIQUserSettings>;
  onSave?: (next: DialIQUserSettings) => Promise<void> | void;
  availableStatuses?: string[];
}

export function DialIQSettings({
  initial,
  onSave,
  availableStatuses,
}: DialIQSettingsProps) {
  // Capture initial state ONCE on mount — don't reactively sync from props.
  // Syncing from props after autosave creates a flip-flop bug where stale
  // parent context clobbers the user's just-saved change.
  const [settings, setSettings] = useState<DialIQUserSettings>(() => ({
    ...DEFAULT_USER_SETTINGS,
    ...(initial || {}),
  }));
  const [mountedSnapshot] = useState(() => JSON.stringify(settings));
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  // Debounced autosave whenever settings change vs mounted snapshot
  const currentKey = useMemo(() => JSON.stringify(settings), [settings]);
  const lastSavedKeyRef = useRef<string>(mountedSnapshot);

  useEffect(() => {
    if (!onSave) return;
    if (currentKey === lastSavedKeyRef.current) return;
    const timer = setTimeout(async () => {
      setSaveStatus("saving");
      try {
        await onSave(settings);
        lastSavedKeyRef.current = currentKey;
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 1800);
      } catch {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus("idle"), 2500);
      }
    }, 450);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentKey]);

  const toggleExcluded = (status: string) => {
    setSettings((s) => {
      const set = new Set(s.excludedStatuses);
      if (set.has(status)) set.delete(status);
      else set.add(status);
      return { ...s, excludedStatuses: Array.from(set) };
    });
  };

  // Merge default statuses with the user's custom pipeline stages.
  // System stages are always shown (match by name = stage.name).
  // Custom stages get added by name as well so users can exclude them too.
  const { data: pipelineStages } = usePipelineStages();
  const excludableList = useMemo(() => {
    if (availableStatuses && availableStatuses.length > 0) return availableStatuses;
    const base = [...DEFAULT_EXCLUDABLE_STATUSES];
    const custom = (pipelineStages || [])
      .filter((s) => !s.isSystem && s.isVisible)
      .map((s) => s.name);
    for (const c of custom) {
      if (!base.includes(c)) base.push(c);
    }
    return base;
  }, [availableStatuses, pipelineStages]);

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border transition-all duration-300",
        "hover:shadow-md hover:shadow-black/[0.03]",
        settings.enabled
          ? "border-primary/20 bg-primary/[0.015]"
          : "border-border",
      )}
    >
        {/* Left accent bar — matches other preference cards */}
        <div
          className={cn(
            "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
            settings.enabled ? "bg-primary" : "bg-transparent",
          )}
        />

        {/* Header row */}
        <div className="flex items-center justify-between p-4 pl-5">
          <div className="flex items-center gap-4">
            <div
              className={cn(
                "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                settings.enabled
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <DialIQMark size={22} />
            </div>
            <div>
              <Label
                htmlFor="dialiq-enabled"
                className="relative inline-flex pr-3 text-[15px] font-semibold leading-none cursor-pointer"
              >
                Dial
                <DialIQMark
                  size={11}
                  className={cn(
                    "absolute right-0 -top-0.5",
                    settings.enabled ? "text-primary" : "text-muted-foreground",
                  )}
                />
              </Label>
              <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
                Leads auto-cycle across Morning, Afternoon, and Evening windows in
                their local timezone. After 5 no-answers in a window, they're moved
                to the next one.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Autosave inline indicator */}
            <AnimatePresence mode="wait">
              {saveStatus === "saving" && (
                <motion.span
                  key="saving"
                  initial={{ opacity: 0, x: 4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving
                </motion.span>
              )}
              {saveStatus === "saved" && (
                <motion.span
                  key="saved"
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  className="flex items-center gap-1 text-[11px] font-medium text-emerald-600"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Saved
                </motion.span>
              )}
              {saveStatus === "error" && (
                <motion.span
                  key="error"
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  className="flex items-center gap-1 text-[11px] font-medium text-rose-600"
                >
                  <AlertCircle className="h-3 w-3" />
                  Couldn't save
                </motion.span>
              )}
            </AnimatePresence>

            <Switch
              id="dialiq-enabled"
              checked={settings.enabled}
              onCheckedChange={(checked) =>
                setSettings((s) => ({ ...s, enabled: !!checked }))
              }
            />
          </div>
        </div>

        {/* Expand section — only when ON */}
        <AnimatePresence initial={false}>
          {settings.enabled && (
            <motion.div {...expand} className="overflow-hidden">
              <div className="px-5 pb-4 pl-[76px]">
                <p className="mb-3 text-xs text-muted-foreground">
                  Tap a stage to skip it from the redial queue
                </p>
                <div className="flex flex-wrap gap-2">
                  {excludableList.map((status) => {
                    const isExcluded = settings.excludedStatuses.includes(status);
                    const stage = (pipelineStages || []).find(
                      (ps) => ps.name === status || ps.systemValue === status,
                    );
                    const color = stage?.color || "#64748B";
                    return (
                      <button
                        type="button"
                        key={status}
                        onClick={() => toggleExcluded(status)}
                        aria-pressed={isExcluded}
                        className={cn(
                          "group relative flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold tracking-tight transition-all duration-200",
                          "ring-1 ring-inset",
                          isExcluded
                            ? "bg-slate-100 text-slate-400 ring-slate-200 line-through decoration-rose-400/80 decoration-[1.5px] hover:ring-rose-200 hover:bg-rose-50/50"
                            : "text-white shadow-sm hover:shadow-md hover:-translate-y-[1px] active:translate-y-0",
                        )}
                        style={
                          isExcluded
                            ? undefined
                            : {
                                backgroundColor: color,
                                boxShadow: `0 1px 2px ${color}40, 0 4px 12px -4px ${color}66`,
                              }
                        }
                      >
                        {/* Active indicator dot */}
                        <span
                          className={cn(
                            "h-1.5 w-1.5 rounded-full transition-all",
                            isExcluded ? "bg-slate-300" : "bg-white/90 shadow-[0_0_8px_rgba(255,255,255,0.6)]",
                          )}
                        />
                        <span>{status}</span>
                        {isExcluded && (
                          <Ban
                            className="h-3 w-3 text-rose-400/80"
                            strokeWidth={2.5}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
                {settings.excludedStatuses.length > 0 && (
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    Skipping{" "}
                    <span className="font-semibold text-rose-600 tabular-nums">
                      {settings.excludedStatuses.length}
                    </span>{" "}
                    stage{settings.excludedStatuses.length === 1 ? "" : "s"} ·
                    leads in these stages will never enter the redial queue
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
    </div>
  );
}

export default DialIQSettings;
